import { useT } from "@magic-md/i18n/react";
import { StretchHorizontal } from "lucide-react";
import {
    memo,
    type MouseEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { getFileKind } from "../lib/file-type";
import {
    PREVIEW_WIDTH_PRESETS,
    type PreviewWidthControl,
    splitPreviewWidthValue,
} from "../lib/preview-width";
import { cn } from "../lib/utils";
import type { PreviewWidth } from "../store/editor-store";
import { useEditorStore } from "../store/editor-store";
import { usePaneChromeContext } from "./PaneContext";

interface PreviewWidthMenuProps {
    currentPreviewWidthLabel: string;
    previewWidthControl: PreviewWidthControl;
    customPreviewWidth: number | null;
    isOpen: boolean;
    onToggle: (event: MouseEvent<HTMLButtonElement>) => void;
    onPresetSelect: (preset: Exclude<PreviewWidthControl, "custom">) => void;
    onReset: () => void;
}

function formatPreviewWidthLabel(
    t: ReturnType<typeof useT>,
    control: PreviewWidthControl,
    customWidth: number | null,
): string {
    if (control === "custom") {
        return customWidth
            ? t("previewWidth.customWithWidth", {
                width: Math.round(customWidth),
            })
            : t("previewWidth.custom");
    }
    return t(`previewWidth.${control}`);
}

const PreviewWidthMenu = memo(function PreviewWidthMenu({
    currentPreviewWidthLabel,
    previewWidthControl,
    customPreviewWidth,
    isOpen,
    onToggle,
    onPresetSelect,
    onReset,
}: PreviewWidthMenuProps) {
    const t = useT("editor");
    return (
        <>
            <button
                type="button"
                className="inline-flex h-6 items-center gap-1 rounded px-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground active:bg-muted"
                onClick={onToggle}
                title={t("previewWidth.adjustTitle", {
                    label: currentPreviewWidthLabel,
                })}
                aria-label={t("previewWidth.adjust")}
                aria-haspopup="menu"
                aria-expanded={isOpen}
            >
                <StretchHorizontal size={14} />
                <span>{currentPreviewWidthLabel}</span>
            </button>
            {isOpen && (
                <div
                    role="menu"
                    className="absolute right-0 top-7 z-30 w-32 overflow-hidden rounded-md border border-border bg-background py-1 text-[11px] shadow-lg"
                >
                    {PREVIEW_WIDTH_PRESETS.map((preset) => (
                        <button
                            key={preset.id}
                            type="button"
                            role="menuitemradio"
                            aria-checked={previewWidthControl === preset.id}
                            className={cn(
                                "flex w-full items-center justify-between px-2 py-1 text-left transition-colors hover:bg-muted",
                                previewWidthControl === preset.id
                                    ? "text-foreground"
                                    : "text-muted-foreground",
                            )}
                            onClick={(event) => {
                                event.stopPropagation();
                                onPresetSelect(preset.id);
                            }}
                        >
                            <span>{t(`previewWidth.${preset.id}`)}</span>
                            <span className="font-mono text-[10px] text-muted-foreground/60">
                                {preset.width === "fill" ? "100%" : preset.width}
                            </span>
                        </button>
                    ))}
                    {previewWidthControl === "custom" && customPreviewWidth && (
                        <div className="border-t border-border px-2 py-1 text-muted-foreground">
                            {t("previewWidth.customWithWidth", {
                                width: Math.round(customPreviewWidth),
                            })}
                        </div>
                    )}
                    <button
                        type="button"
                        role="menuitem"
                        className="flex w-full items-center px-2 py-1 text-left text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        onClick={(event) => {
                            event.stopPropagation();
                            onReset();
                        }}
                    >
                        {t("previewWidth.reset")}
                    </button>
                </div>
            )}
        </>
    );
});

export const PanePreviewWidthControl = memo(function PanePreviewWidthControl() {
    const t = useT("editor");
    const { paneId, previewWidth, filePath } = usePaneChromeContext();
    const fileKind = getFileKind(filePath);
    const isMarkdownView = fileKind === "markdown";
    const setPanePreviewWidth = useEditorStore((s) => s.setPanePreviewWidth);
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const { control: previewWidthControl, customWidth: customPreviewWidth } =
        useMemo(() => splitPreviewWidthValue(previewWidth), [previewWidth]);
    const currentPreviewWidthLabel = formatPreviewWidthLabel(
        t,
        previewWidthControl,
        customPreviewWidth,
    );

    const setPreviewWidth = useCallback(
        (width: PreviewWidth) => setPanePreviewWidth(paneId, width),
        [paneId, setPanePreviewWidth],
    );
    const handlePresetSelect = useCallback(
        (preset: Exclude<PreviewWidthControl, "custom">) => {
            setPreviewWidth(preset);
            setIsOpen(false);
        },
        [setPreviewWidth],
    );
    const toggleMenu = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setIsOpen((open) => !open);
    }, []);
    const resetPreviewWidth = useCallback(() => {
        setPreviewWidth("fill");
        setIsOpen(false);
    }, [setPreviewWidth]);

    useEffect(() => {
        if (!isOpen || typeof window === "undefined") return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setIsOpen(false);
            }
        };
        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target;
            if (target instanceof Node && menuRef.current?.contains(target)) {
                return;
            }
            setIsOpen(false);
        };

        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("pointerdown", handlePointerDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("pointerdown", handlePointerDown);
        };
    }, [isOpen]);

    if (!filePath) return null;

    if (!isMarkdownView) {
        return null;
    }

    return (
        <div
            ref={menuRef}
            className="relative shrink-0"
            data-testid="pane-preview-width-control"
        >
            <PreviewWidthMenu
                currentPreviewWidthLabel={currentPreviewWidthLabel}
                previewWidthControl={previewWidthControl}
                customPreviewWidth={customPreviewWidth}
                isOpen={isOpen}
                onToggle={toggleMenu}
                onPresetSelect={handlePresetSelect}
                onReset={resetPreviewWidth}
            />
        </div>
    );
});
